import { datadogRum } from '@datadog/browser-rum';
import Cookies from 'js-cookie';
import { eventChannel } from 'redux-saga';
import {
  call,
  delay,
  put,
  race,
  take,
  type SagaGenerator,
} from 'typed-redux-saga';
import userflow from 'userflow.js';
import { getItem, removeItem, setItem } from '@owl-frontend/components';
import {
  AsyncActionState,
  asyncActionStateMatchers,
  invoke,
  setupSlice,
} from '@owl-frontend/redux';
import { createLogger } from '@owl-lib/logger';
import { HTTPError } from '../../api/fetch';
import { type User, UserRole } from '../../api/interface';
import * as sessionApi from '../../api/session/client';
import { SESSION_TIMEOUT_LIMIT } from '../../api/session/client';

const logger = createLogger(__filename);

if (RUM_APPLICATION_ID && RUM_CLIENT_TOKEN) {
  datadogRum.init({
    env: ENV,
    version: __version,
    applicationId: RUM_APPLICATION_ID,
    clientToken: RUM_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: 'platform',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100, // if not included, the default is 100
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    defaultPrivacyLevel: 'mask',
  });

  datadogRum.setGlobalContextProperty('viewport', {
    width: window.screen.width,
    height: window.screen.height,
  });

  datadogRum.setGlobalContextProperty(
    'darkMode',
    Cookies.get('darkMode') === 'true'
  );

  datadogRum.startSessionReplayRecording();
}

if (USERFLOW_KEY) {
  userflow.init(USERFLOW_KEY);
}

function* current(): SagaGenerator<User | undefined> {
  const { sessionCurrent, sessionTimeout } = yield* race({
    sessionCurrent: call(sessionApi.current),
    sessionTimeout: delay(SESSION_TIMEOUT_LIMIT),
  });

  if (sessionTimeout) {
    throw new Error('TIMED_OUT');
  }

  if (RUM_APPLICATION_ID && RUM_CLIENT_TOKEN && sessionCurrent) {
    datadogRum.setUser({
      id: sessionCurrent.user_id || sessionCurrent.email,
      name: sessionCurrent.email,
      email: sessionCurrent.email,
    });

    yield* call(
      datadogRum.setGlobalContextProperty,
      'tenant',
      sessionCurrent.tenant_id
    );
  }

  if (sessionCurrent) {
    yield* call(setItem, 'tenant', sessionCurrent.tenant_id);
  }

  return sessionCurrent;
}

const POLL_INTERVAL_MS = 60_000; // 1 minute

function createVisibilityChannel() {
  return eventChannel<'visible'>((emit) => {
    const handler = () => {
      if (document.visibilityState === 'visible') {
        emit('visible');
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  });
}

export function* pollRefreshToken() {
  const visibilityChannel = createVisibilityChannel();
  try {
    while (true) {
      try {
        yield* race({
          timer: delay(POLL_INTERVAL_MS),
          visible: take(visibilityChannel),
        });
        const { timeout } = yield* race({
          request: call(sessionApi.requestNewToken, {
            authCode: null,
            state: null,
          }),
          timeout: delay(SESSION_TIMEOUT_LIMIT),
        });
        if (timeout) {
          logger.error('token refresh timed out');
        }
      } catch (e) {
        if (e instanceof HTTPError && e.response.status >= 400) {
          logger.error('pollRefreshToken: auth error, re-authenticating', e);
          throw e;
        }
        logger.error('pollRefreshToken iteration failed; continuing', e);
        yield* delay(POLL_INTERVAL_MS);
      }
    }
  } finally {
    visibilityChannel.close();
  }
}

function* requestLogout() {
  datadogRum.stopSessionReplayRecording();
  if (USERFLOW_KEY) {
    userflow.reset();
  }

  return yield* call(sessionApi.requestLogout);
}

const LOGIN_DISCLAIMER_STORAGE_KEY = 'owl_platform_login_disclaimer_ack';

export const getLoginDisclaimerAck = () =>
  getItem(LOGIN_DISCLAIMER_STORAGE_KEY);

export const clearLoginDisclaimerAck = () => {
  removeItem(LOGIN_DISCLAIMER_STORAGE_KEY);
};

const setLoginDisclaimerAck = () => {
  setItem(LOGIN_DISCLAIMER_STORAGE_KEY, '1');
};

function* onAcceptLoginDisclaimer(): SagaGenerator<void> {
  yield* call(setLoginDisclaimerAck);
}

const VERSION_POLL_INTERVAL_MS = 60_000;

function* pollVersion() {
  // Skip polling to avoid 404s when running locally.
  if (!PLATFORM_APP_VERSION) {
    return;
  }

  yield* delay(VERSION_POLL_INTERVAL_MS);

  while (true) {
    try {
      const data: { version?: string } = yield* call(sessionApi.version);
      if (data?.version && data.version !== PLATFORM_APP_VERSION) {
        yield* put({
          type: 'session/setVersionRefreshAvailable',
          payload: true,
        });
        return;
      }
    } catch (e) {
      logger.error('version polling failed', e);
    }

    yield* delay(VERSION_POLL_INTERVAL_MS);
  }
}
export interface SessionState {
  current?: User;
  versionRefreshAvailable?: boolean;
  darkMode: boolean;
  actions: {
    current: AsyncActionState;
    pollRefreshToken: AsyncActionState;
    pollVersion: AsyncActionState;
    requestLogin: AsyncActionState<{ url: string; message: string }>;
    requestToken: AsyncActionState;
    requestLogout: AsyncActionState;
  };
}

const initialState: SessionState = {
  versionRefreshAvailable: false,
  darkMode: Cookies.get('darkMode') === 'true',
  actions: {
    current: { status: 'uninitialized' },
    pollRefreshToken: { status: 'uninitialized' },
    pollVersion: { status: 'uninitialized' },
    requestLogin: { status: 'uninitialized' },
    requestToken: { status: 'uninitialized' },
    requestLogout: { status: 'uninitialized' },
  },
};

const slice = setupSlice('session', initialState)
  .addAsyncSagas({
    current,
    requestLogin: invoke(sessionApi.requestLogin),
    requestToken: invoke(sessionApi.requestNewToken),
    requestLogout,
  })
  .addAsyncSagas({ pollRefreshToken, pollVersion }, { timeoutLimit: -1 })
  .addSagas({
    onAcceptLoginDisclaimer,
  })
  .addReducers({
    setVersionRefreshAvailable: (
      state,
      action: { payload: boolean | undefined }
    ) => {
      state.versionRefreshAvailable = action.payload ?? true;
    },
    setDarkMode: (state, action: { payload: boolean }) => {
      state.darkMode = action.payload;
    },
  })
  .addReducers({
    'current/fulfilled': (state, action) => {
      const user = action.payload;
      if (USERFLOW_KEY && user) {
        userflow.identify(user.client_user_id, {
          name: user.full_name,
          email: user.email,
          role: user.roles.join(', '),
          signed_up_at: user.created_at,
        });
      }
      state.current = {
        ...user,
        has_manager_role:
          user?.roles?.includes(UserRole.Manager) ||
          user?.roles?.includes(UserRole.Manager.toLowerCase()),
      } as User;
    },
    'requestLogin/fulfilled': (_, action) => {
      window.location.replace(action.payload.url);
    },
    'requestLogout/fulfilled': (_, action) => {
      sessionStorage.removeItem('filters');
      sessionStorage.removeItem('pagination');
      clearLoginDisclaimerAck();
      window.location.replace(action.payload.url);
    },
  });

export const actions = slice.actions;
export default slice.addExtra(asyncActionStateMatchers(actions).all());
